import React, { useEffect, useState } from 'react';
import { onGameEvent, emitGameEvent } from '../../core/GameEvents';

// Color palette matching the game
const PALETTE = {
  DARK: '#0f1b26',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
  YELLOW: '#fbbf24',
  RED: '#ef4444',
  GREEN: '#22c55e',
};

// Utility type to icon color mapping (only active/accionable utilities)
const UTILITY_COLORS: Record<string, string> = {
  warp_disruptor: '#ff4488',
  beacon_deployer: '#ffaa00',
  cargo_scanner: '#aa88ff',
  fighter_drone_bay: '#44ddff',
  miner_drone_bay: '#44ff88',
};

// Utility type to icon symbol
const UTILITY_ICONS: Record<string, string> = {
  warp_disruptor: '⊘',
  beacon_deployer: '◈',
  cargo_scanner: '⊞',
  fighter_drone_bay: '⊛',
  miner_drone_bay: '⊛',
};

// Short display names
const UTILITY_SHORT_NAMES: Record<string, string> = {
  warp_disruptor: 'DISRUPT',
  beacon_deployer: 'BEACON',
  cargo_scanner: 'SCAN',
  fighter_drone_bay: 'FIGHTER',
  miner_drone_bay: 'MINER',
};

export interface HotbarSlot {
  slotIndex: number;         // 0-4 (maps to keys 1-5)
  equipped: boolean;
  utilityType: string;       // e.g. 'booster', 'hull_plating'
  utilityName: string;       // e.g. 'Thruster Booster T2'
  utilityId: string;         // equipment ID
  tier: number;
  isActive: boolean;         // Currently active (boosting, scanning, etc.)
  isOnCooldown: boolean;     // Currently on cooldown
  cooldownPercent: number;   // 0-1, how much cooldown remains
  cooldownRemaining: number; // ms remaining
  isPassive: boolean;        // Passive utility (no activation)
  energyPercent?: number;    // For booster: 0-1 energy level
}

// Single hotbar slot component
function HotbarSlotView({ slot, keyLabel }: { slot: HotbarSlot | null; keyLabel: string }) {
  const isActive = slot?.isActive ?? false;
  const isOnCooldown = slot?.isOnCooldown ?? false;
  const isPassive = slot?.isPassive ?? false;
  const utilityType = slot?.utilityType ?? '';
  const color = UTILITY_COLORS[utilityType] || PALETTE.DIM;
  const icon = UTILITY_ICONS[utilityType] || '·';
  const shortName = UTILITY_SHORT_NAMES[utilityType] || '';

  const handleClick = () => {
    if (slot && !isPassive && !isOnCooldown) {
      emitGameEvent('activateUtilitySlot', slot.slotIndex);
    }
  };

  return (
    <div
      onClick={slot && !isPassive ? handleClick : undefined}
      style={{
        width: '64px',
        height: '64px',
        position: 'relative',
        backgroundColor: isActive ? `${color}22` : `${PALETTE.DARK}cc`,
        border: `2px solid ${slot ? (isActive ? color : `${color}66`) : `${PALETTE.DIM}33`}`,
        borderRadius: '6px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: slot && !isPassive ? 'pointer' : 'default',
        pointerEvents: 'auto',
        transition: 'border-color 0.15s, background-color 0.15s',
        boxShadow: isActive ? `0 0 12px ${color}44, inset 0 0 8px ${color}22` : 'none',
        opacity: isPassive ? 0.5 : 1,
        overflow: 'hidden',
      }}
    >
      {/* Cooldown overlay - sweeps down from top */}
      {isOnCooldown && slot && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: `${(slot.cooldownPercent) * 100}%`,
            backgroundColor: `${PALETTE.DARK}bb`,
            transition: 'height 0.1s linear',
            zIndex: 2,
          }}
        />
      )}

      {/* Energy bar for booster (vertical bar on left edge) */}
      {slot && utilityType === 'booster' && slot.energyPercent !== undefined && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '4px',
            height: `${slot.energyPercent * 100}%`,
            backgroundColor: isActive ? '#00ffff' : PALETTE.TEAL,
            transition: 'height 0.1s linear',
            zIndex: 3,
            borderRadius: '0 2px 2px 0',
          }}
        />
      )}

      {/* Icon */}
      <span style={{
        fontSize: '11px',
        lineHeight: 1,
        color: slot ? color : `${PALETTE.DIM}44`,
        zIndex: 1,
        filter: isOnCooldown ? 'brightness(0.5)' : 'none',
      }}>
        {slot ? icon : ''}
      </span>

      {/* Short name */}
      {slot && (
        <span style={{
          fontSize: '10px',
          fontWeight: 600,
          color: isActive ? color : PALETTE.DIM,
          zIndex: 1,
          marginTop: '3px',
          letterSpacing: '0.5px',
        }}>
          {shortName}
        </span>
      )}

      {/* Cooldown remaining text */}
      {isOnCooldown && slot && slot.cooldownRemaining > 0 && (
        <span style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '11px',
          fontWeight: 600,
          color: PALETTE.LIGHT,
          zIndex: 3,
          textShadow: '0 0 6px black, 0 0 3px black',
        }}>
          {Math.ceil(slot.cooldownRemaining / 1000)}
        </span>
      )}

      {/* Key binding label - top left */}
      <div style={{
        position: 'absolute',
        top: '2px',
        left: '4px',
        fontSize: '11px',
        color: PALETTE.LIGHT,
        zIndex: 1,
        fontWeight: 600,
        opacity: 0.6,
        textShadow: '0 0 3px black',
      }}>
        {keyLabel}
      </div>

      {/* Tier indicator */}
      {slot && (
        <div style={{
          position: 'absolute',
          bottom: '2px',
          right: '4px',
          fontSize: '9px',
          color: color,
          zIndex: 1,
          fontWeight: 600,
          opacity: 0.7,
        }}>
          T{slot.tier}
        </div>
      )}

      {/* Active glow pulse */}
      {isActive && (
        <div style={{
          position: 'absolute',
          inset: 0,
          border: `2px solid ${color}`,
          borderRadius: '6px',
          animation: 'utilityPulse 1.5s ease-in-out infinite',
          zIndex: 4,
          pointerEvents: 'none',
        }} />
      )}
    </div>
  );
}

export function UtilityHotbar() {
  const [slots, setSlots] = useState<(HotbarSlot | null)[]>([null, null, null, null, null]);

  useEffect(() => {
    const unsub = onGameEvent('utilityHotbarUpdate', (data) => {
      setSlots(data.slots);
    });
    return unsub;
  }, []);

  // Inject pulse keyframes
  useEffect(() => {
    const styleId = 'utility-hotbar-keyframes';
    if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes utilityPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Don't render if no utilities equipped at all
  const hasAnyUtility = slots.some(s => s !== null);
  if (!hasAnyUtility) return null;

  // Only render slots that have utilities (no empty trailing slots)
  const activeSlotCount = slots.filter(s => s !== null).length;

  return (
    <div style={{
      position: 'absolute',
      bottom: '20px',
      right: '20px',
      display: 'flex',
      gap: '6px',
      padding: '8px',
      backgroundColor: `${PALETTE.PANEL}cc`,
      borderRadius: '8px',
      border: `1px solid ${PALETTE.DIM}44`,
      pointerEvents: 'none',
    }}>
      {slots.slice(0, activeSlotCount).map((slot, i) => (
        <HotbarSlotView
          key={i}
          slot={slot}
          keyLabel={`${i + 1}`}
        />
      ))}
    </div>
  );
}
